import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ProviderBadges, Stars } from "@/components/Badges";
import { LoadingScreen } from "@/components/ProtectedRoute";
import { MapPin, Clock, Phone, CheckCircle2, Calendar, FileText, Repeat } from "lucide-react";

export default function ProviderProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);

  useEffect(() => { api.get(`/providers/${id}`).then((r) => setP(r.data)).catch(() => {}); }, [id]);
  if (!p) return <LoadingScreen />;

  return (
    <Layout>
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img src={p.photos?.[0] || p.profile_photo} alt={p.business_name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-lawn-ink/90 to-lawn-ink/20" />
      </div>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-24 relative">
        <div className="bg-white rounded-2xl border border-border p-6 sm:p-8 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6">
            <div>
              <ProviderBadges badges={p.badges} size="md" />
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-lawn-ink mt-3">{p.business_name}</h1>
              <div className="mt-2"><Stars rating={p.rating} count={p.review_count} /></div>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {p.city}, {p.state}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {p.response_time_hours}h avg response</span>
                {p.phone && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" /> {p.phone}</span>}
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:min-w-[200px]">
              <Button data-testid="book-now-btn" onClick={() => navigate(`/book/${p.provider_id}`)} className="rounded-full bg-lawn hover:bg-lawn-dark font-semibold gap-2 h-11"><Calendar className="w-4 h-4" /> Book Now</Button>
              <Button data-testid="request-quote-btn" onClick={() => navigate(`/quote/${p.provider_id}`)} variant="outline" className="rounded-full font-semibold gap-2 h-11"><FileText className="w-4 h-4" /> Request Quote</Button>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mt-8">
            <div className="lg:col-span-2 space-y-8">
              <section>
                <h2 className="font-display text-xl font-bold text-lawn-ink mb-3">About</h2>
                <p className="text-muted-foreground leading-relaxed">{p.description}</p>
              </section>

              <section>
                <h2 className="font-display text-xl font-bold text-lawn-ink mb-4">Services & Pricing</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {p.services?.map((s) => (
                    <div key={s.category} className="border border-border rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-lawn-ink text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground">{s.time_estimate}</p>
                      </div>
                      <span className="font-display font-extrabold text-lawn">${s.price}</span>
                    </div>
                  ))}
                </div>
              </section>

              {p.before_after?.length > 0 && (
                <section>
                  <h2 className="font-display text-xl font-bold text-lawn-ink mb-4">Before & After</h2>
                  <div className="grid grid-cols-2 gap-4">
                    {p.before_after.map((ba, i) => (
                      <React.Fragment key={i}>
                        <div className="relative rounded-xl overflow-hidden"><img src={ba.before} alt="before" className="w-full h-40 object-cover" /><span className="absolute top-2 left-2 bg-lawn-ink/80 text-white text-xs px-2 py-0.5 rounded-full">Before</span></div>
                        <div className="relative rounded-xl overflow-hidden"><img src={ba.after} alt="after" className="w-full h-40 object-cover" /><span className="absolute top-2 left-2 bg-lawn text-white text-xs px-2 py-0.5 rounded-full">After</span></div>
                      </React.Fragment>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <h2 className="font-display text-xl font-bold text-lawn-ink mb-4">Reviews ({p.review_count})</h2>
                <div className="space-y-4">
                  {p.reviews?.length ? p.reviews.map((r) => (
                    <div key={r.review_id} className="border border-border rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-lawn-light flex items-center justify-center text-lawn-dark font-bold text-sm">{r.customer_name?.[0]}</div>
                          <div>
                            <p className="font-semibold text-sm text-lawn-ink">{r.customer_name}</p>
                            {r.verified && <span className="text-[11px] text-lawn flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Verified</span>}
                          </div>
                        </div>
                        <Stars rating={r.rating} />
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">{r.text}</p>
                      {r.provider_response && <div className="mt-3 bg-muted rounded-lg p-3 text-sm"><span className="font-semibold text-lawn-ink">Response from {p.business_name}: </span>{r.provider_response}</div>}
                    </div>
                  )) : <p className="text-muted-foreground text-sm">No reviews yet.</p>}
                </div>
              </section>
            </div>

            <aside className="space-y-4">
              <div className="border border-border rounded-2xl p-5">
                <h3 className="font-display font-bold text-lawn-ink mb-3">Details</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-lawn" /> {p.verified ? "Verified provider" : "Provider"}</li>
                  <li className="flex items-center gap-2"><Repeat className="w-4 h-4 text-lawn" /> Recurring plans available</li>
                  <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-lawn" /> Available: {p.availability?.join(", ")}</li>
                  {p.instant_booking && <li className="flex items-center gap-2"><Calendar className="w-4 h-4 text-lawn" /> Instant booking</li>}
                </ul>
              </div>
              <div className="border border-border rounded-2xl p-5">
                <h3 className="font-display font-bold text-lawn-ink mb-3">Service area</h3>
                <div className="flex flex-wrap gap-1.5">{p.service_areas?.map((z) => <span key={z} className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">{z}</span>)}</div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </Layout>
  );
}
